import styled from 'styled-components'
import AuthService from 'services/auth'
import CashFlow from 'services/CashFlow'
import DailyGoals from './DailyGoals'
import GoalsHeader, { WRAP_STYLE } from './GoalsHeader'
import { formatPercentage } from 'pages/Financial/Dre/utils'
import {
  Section,
  SectionTitle,
  TableWrapper,
  Table,
  LabelCell,
  ValueCell,
  MoneyText,
  PercentageText
} from 'pages/Financial/Dre/styles'

/*
  Larguras mínimas, para a tabela caber na tela sem rolagem lateral: a coluna de cada dia não tem
  largura fixa, divide o que sobra (a tabela é `table-layout: fixed` com 100% de largura), e só abaixo
  da soma dos mínimos a tabela rola de lado. O padding lateral das células de valor é menor que o
  padrão, que deixaria cada coluna mais larga do que "R$ 1.234,56" precisa.
*/
const LABEL_WIDTH = 168
const DAY_MIN_WIDTH = 92
const TOTAL_WIDTH = 100
const DIFFERENCE_WIDTH = 104
const ACHIEVED_WIDTH = 80

const CompactTable = styled(Table)`
  th:not(:first-child), td:not(:first-child) {
    padding-left: 6px;
    padding-right: 6px;
  }
`

// O que cada especialidade compara, nas seis linhas da planilha: a meta e o realizado de cada valor.
// `salon` é o nome do salão, que a planilha põe no meio do rótulo ("Projetado Fulana Alongamento").
const linesOf = salon => [
  { projected: 'Projetado', realized: 'Realizado', meta: 'metaValue', done: 'realizedValue' },
  { projected: 'Comissão Projetado', realized: 'Comissão Realizado', meta: 'metaComission', done: 'realizedComission' },
  { projected: `Projetado ${salon}`, realized: `Realizado ${salon}`, meta: 'metaSalon', done: 'realizedSalon' }
]

// Quem entra como profissional vê o próprio nome em `getUser()`, não o do salão.
const salonName = () => (
  AuthService.getUserType() === 'professional' ? 'salão' : (AuthService.getUser()?.name ?? 'salão')
)

const renderMoney = value => (value === 0 ? '–' : <MoneyText value={value}/>)

const renderPercentage = (done, meta) => {
  const achievement = CashFlow.achievement(done, meta)

  return achievement === null ? '–' : <PercentageText>{formatPercentage(achievement)}</PercentageText>
}

// As duas linhas de um valor: projetado e realizado, com o total da semana, a diferença e o atingido.
function ValueRows({ line, item, columns, kind, projectedLabel, realizedLabel }) {
  const { meta, done } = line
  const difference = Math.round((item.total[done] - item.total[meta]) * 100) / 100

  return (
    <>
      <tr>
        <LabelCell kind={kind} style={WRAP_STYLE}>{projectedLabel ?? line.projected}</LabelCell>
        {columns.map(column => (
          <ValueCell key={column.date} kind={kind}>{renderMoney(item.cells[column.index][meta])}</ValueCell>
        ))}
        <ValueCell kind='total'>{renderMoney(item.total[meta])}</ValueCell>
        <ValueCell kind={kind}/>
        <ValueCell kind={kind}/>
      </tr>
      <tr>
        <LabelCell kind={kind} style={WRAP_STYLE}>{realizedLabel ?? line.realized}</LabelCell>
        {columns.map(column => (
          <ValueCell key={column.date} kind={kind}>{renderMoney(item.cells[column.index][done])}</ValueCell>
        ))}
        <ValueCell kind='total'>{renderMoney(item.total[done])}</ValueCell>
        <ValueCell kind={kind}>
          {difference === 0 ? '–' : <MoneyText value={difference}/>}
        </ValueCell>
        <ValueCell kind={kind}>{renderPercentage(item.total[done], item.total[meta])}</ValueCell>
      </tr>
    </>
  )
}

function CategoryTable({ category, columns }) {
  const lines = linesOf(salonName())

  return (
    <TableWrapper framed>
      <CompactTable style={{
        minWidth: LABEL_WIDTH + columns.length * DAY_MIN_WIDTH + TOTAL_WIDTH + DIFFERENCE_WIDTH + ACHIEVED_WIDTH
      }}>
        <colgroup>
          <col style={{ width: LABEL_WIDTH }}/>
          {columns.map(column => <col key={column.date}/>)}
          <col style={{ width: TOTAL_WIDTH }}/>
          <col style={{ width: DIFFERENCE_WIDTH }}/>
          <col style={{ width: ACHIEVED_WIDTH }}/>
        </colgroup>
        <GoalsHeader title={category.name} columns={columns} trailing={['Total', 'Diferença', 'Atingido']}/>
        <tbody>
          <ValueRows
            line={lines[0]}
            item={category}
            columns={columns}
            kind='total'
            projectedLabel='Projetado Total'
            realizedLabel='Realizado Total'
          />
          {category.specialties.map(specialty => (
            <SpecialtyRows key={specialty.id} specialty={specialty} lines={lines} columns={columns}/>
          ))}
        </tbody>
      </CompactTable>
    </TableWrapper>
  )
}

function SpecialtyRows({ specialty, lines, columns }) {
  return (
    <>
      {lines.map(line => (
        <ValueRows
          key={line.meta}
          line={line}
          item={specialty}
          columns={columns}
          projectedLabel={`${line.projected} ${specialty.name}`}
          realizedLabel={`${line.realized} ${specialty.name}`}
        />
      ))}
    </>
  )
}

/*
  "Meta $": o que o salão projeta e o que entregou, em reais, na categoria escolhida e, dentro dela,
  por especialidade, nas mesmas linhas da planilha. O projetado é a meta de atendimentos (pelo serviço
  mais lucrativo por hora) × o preço cadastrado; a comissão, a do cadastro de cada profissional; e o
  salão, o que sobra depois dela. Diferença é realizado − projetado, e Atingido, realizado ÷ projetado.
*/
export default function GoalsByValue({ year, month, percentage, filter, onFilterChange }) {
  return (
    <DailyGoals
      year={year}
      month={month}
      percentage={percentage}
      filter={filter}
      onFilterChange={onFilterChange}
    >
      {({ category, columns }) => (
        <Section>
          <SectionTitle>{category.name}</SectionTitle>
          <CategoryTable category={category} columns={columns}/>
        </Section>
      )}
    </DailyGoals>
  )
}
